import { useOutletContext } from "react-router-dom";
import { Link } from "react-router-dom";
import { Heart, Users, Calendar, Image, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const HERO_IMAGE = "./assets/images/Sport_Eldorado_Banner.webp";

const features = [
  {
    icon: Users,
    title: "Voor Iedereen",
    description: "Ongeacht achtergrond, niveau of financiële situatie - sport is voor iedereen toegankelijk."
  },
  {
    icon: Heart,
    title: "Gemeenschap",
    description: "We bouwen samen aan een sterke gemeenschap waar jongeren zich thuis voelen."
  },
  {
    icon: Calendar,
    title: "Activiteiten",
    description: "Regelmatige sportactiviteiten, workshops en evenementen in de wijken van Gent."
  }
];

export default function SportEldorado() {
  const context = useOutletContext();
  const isDark = context?.isDark ?? true;

  return (
    <div className="min-h-screen" data-testid="sport-eldorado-page">
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="hero-gradient absolute inset-0" />
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-end pb-12">
          <div>
            <p className="text-[hsl(48,96%,53%)] font-medium mb-2">Een initiatief van Futsal Gent</p>
            <h1 className="text-4xl md:text-6xl font-black text-white font-['Chivo']">SPORT ELDORADO</h1>
            <p className="text-slate-300 mt-4 max-w-2xl text-lg">
              Sport toegankelijk maken voor alle jongeren in de buurten van Gent, 
              ongeacht hun achtergrond of niveau.
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Introduction */}
        <Card className={`mb-12 ${isDark ? 'bg-gradient-to-r from-blue-900/30 to-slate-900/50 border-white/10' : 'bg-gradient-to-r from-blue-50 to-white border-slate-200 shadow-md'}`}>
          <CardContent className="py-10">
            <h2 className={`text-3xl font-black font-['Chivo'] mb-6 text-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Over Ons
            </h2>
            <p className={`text-lg leading-relaxed text-center max-w-3xl mx-auto ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              Sport Eldorado is een sociaal initiatief opgericht onder Futsal Gent met als doel 
              sport toegankelijk te maken voor alle jongeren in de buurten van Gent. Wij geloven 
              dat sport een krachtig middel is om jongeren samen te brengen, hun talenten te 
              ontwikkelen en hen te helpen groeien als persoon - ongeacht hun achtergrond of niveau.
            </p>
          </CardContent>
        </Card>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <Card key={feature.title} className={`text-center ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}`}>
                <CardContent className="pt-8 pb-6">
                  <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${isDark ? 'bg-[hsl(48,96%,53%)]/20' : 'bg-amber-100'}`}>
                    <Icon className={`h-8 w-8 ${isDark ? 'text-[hsl(48,96%,53%)]' : 'text-amber-600'}`} />
                  </div>
                  <h3 className={`font-bold text-xl mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>{feature.title}</h3>
                  <p className={`${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{feature.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          <Link to="/sport-eldorado/missie-visie" className="group">
            <Card className={`h-full transition-all group-hover:scale-[1.02] ${isDark ? 'bg-slate-900/50 border-white/10 group-hover:border-[hsl(217,91%,60%)]/50' : 'bg-white border-slate-200 shadow-md group-hover:shadow-lg'}`}>
              <CardContent className="py-8 text-center">
                <Heart className={`h-10 w-10 mx-auto mb-4 ${isDark ? 'text-[hsl(217,91%,60%)]' : 'text-blue-600'}`} />
                <h3 className={`font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Missie & Visie</h3>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Ontdek onze doelen</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/sport-eldorado/galerij" className="group">
            <Card className={`h-full transition-all group-hover:scale-[1.02] ${isDark ? 'bg-slate-900/50 border-white/10 group-hover:border-[hsl(217,91%,60%)]/50' : 'bg-white border-slate-200 shadow-md group-hover:shadow-lg'}`}>
              <CardContent className="py-8 text-center">
                <Image className={`h-10 w-10 mx-auto mb-4 ${isDark ? 'text-[hsl(217,91%,60%)]' : 'text-blue-600'}`} />
                <h3 className={`font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Galerij</h3>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Bekijk onze foto's</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/sport-eldorado/kalender" className="group">
            <Card className={`h-full transition-all group-hover:scale-[1.02] ${isDark ? 'bg-slate-900/50 border-white/10 group-hover:border-[hsl(217,91%,60%)]/50' : 'bg-white border-slate-200 shadow-md group-hover:shadow-lg'}`}>
              <CardContent className="py-8 text-center">
                <Calendar className={`h-10 w-10 mx-auto mb-4 ${isDark ? 'text-[hsl(217,91%,60%)]' : 'text-blue-600'}`} />
                <h3 className={`font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Kalender</h3>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Bekijk activiteiten</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/contact" className="group">
            <Card className={`h-full transition-all group-hover:scale-[1.02] ${isDark ? 'bg-slate-900/50 border-white/10 group-hover:border-[hsl(217,91%,60%)]/50' : 'bg-white border-slate-200 shadow-md group-hover:shadow-lg'}`}>
              <CardContent className="py-8 text-center">
                <Users className={`h-10 w-10 mx-auto mb-4 ${isDark ? 'text-[hsl(217,91%,60%)]' : 'text-blue-600'}`} />
                <h3 className={`font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Contact</h3>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Neem contact op</p>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* CTA */}
        <Card className={`${isDark ? 'bg-gradient-to-r from-[hsl(48,96%,53%)]/20 to-slate-900/50 border-[hsl(48,96%,53%)]/30' : 'bg-gradient-to-r from-amber-50 to-white border-amber-200 shadow-md'}`}>
          <CardContent className="py-12 text-center">
            <h3 className={`text-2xl font-black font-['Chivo'] mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Doe Mee!
            </h3>
            <p className={`max-w-2xl mx-auto mb-6 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              Ben je geïnteresseerd om mee te doen aan onze activiteiten of wil je als vrijwilliger 
              helpen? Neem contact met ons op en ontdek hoe je deel kunt uitmaken van Sport Eldorado.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild className="bg-[hsl(48,96%,53%)] text-slate-900 hover:bg-[hsl(48,96%,63%)]">
                <Link to="/sport-eldorado/kalender">
                  Bekijk Activiteiten <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className={isDark ? 'border-white/20 text-white hover:bg-white/10' : 'border-slate-300'}>
                <Link to="/contact">
                  Contact Ons
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
